import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { contentfulApiCompetitionToCompetitionProps } from '../../transformers/contentfulApiCompetitionToCompetitionProps';
import { useContentMetafieldData } from '@jbhi-fi/components/src/hooks/useContentMetafieldData/useContentMetafieldData';
import { UseContentProps } from '@jbhi-fi/components/src/types';
import { CompetitionProps } from '../../views/Competition/Competition';
import { contentfulApiCompetitionToStackSchema } from '../../transformers/stackSchema/contentfulApiCompetitionToStackSchemaProps';
import { StackSchema, StackSchemaProps } from '../../components/StackSchema/StackSchema';
import { ContentfulCompetition, EntryType } from '@jbhi-fi/apis/src/contentful/types';
import { useContentfulEntries } from '@jbhi-fi/components/src/hooks/useContentfulEntries/useContentfulEntries';
import { serviceHelper } from '../../../../../config/service-helper';
import { format, isValid } from 'date-fns';
import { SessionStorageKeys, setSession } from '@jbhi-fi/utils/src/sessionStorage';

export const CompetitionPropsContext = createContext<UseContentProps<CompetitionProps> | null>(null);

export const CompetitionPropsProvider = ({ children }: { children: ReactNode }) => {
  const competitionRef = useRef<HTMLDivElement>(null);
  const metafieldData = useContentMetafieldData<ContentfulCompetition>();
  const { entries, isLoading, error } = useContentfulEntries<ContentfulCompetition>({
    filter: {
      content_type: EntryType.competition,
      'sys.id': metafieldData.sys.id,
    },
    autoRefresh: window.Jbhifi.isContentPreview,
  });
  const data = entries?.length ? entries[0] : null;
  const isCompetitionPerksOnly = data?.fields?.perksMemberExclusive;
  const isUserPerks = window.Jbhifi?.customerDetails?.isPerks;
  const customerFirstName = window.Jbhifi?.customerDetails?.firstName ?? '';
  const alreadyEnteredCompetitionText = window.Jbhifi?.alreadyEnteredCompetitionText;
  const [competition, setCompetition] = useState<CompetitionProps>(
    contentfulApiCompetitionToCompetitionProps(metafieldData, { customerFirstName, alreadyEnteredCompetitionText })
  );
  const [enteredOn, setEnteredOn] = useState<string | undefined>(undefined);

  useEffect(() => {
    setCompetition(
      contentfulApiCompetitionToCompetitionProps(data || metafieldData, {
        customerFirstName,
        alreadyEnteredCompetitionText,
      })
    );
  }, [data]);

  useEffect(() => {
    const getCompetitionEntered = async () => {
      const response = await fetch(`${serviceHelper.themeApisUrl}/competitions/${competition.competitionId}`, {
        credentials: 'include',
      });

      if (!response.ok) return;

      const responseText = await response.text();
      const timestamp = new Date(responseText);
      if (isValid(timestamp)) {
        setEnteredOn(format(timestamp, 'd MMM yyyy'));
      }
    };

    const onSubmit = () => {
      if (isCompetitionPerksOnly && isUserPerks) {
        setSession(SessionStorageKeys.CompetitionSubmitted, competition.competitionId);
      }
    };

    if (!isLoading && competition) {
      if (isCompetitionPerksOnly && isUserPerks) {
        getCompetitionEntered();
      } else {
        setEnteredOn(undefined);
      }
    }

    const competitionForm = competitionRef.current;
    competitionForm?.addEventListener('submit', onSubmit);

    return () => {
      competitionForm?.removeEventListener('submit', onSubmit);
    };
  }, [isLoading, competition]);

  return (
    <div ref={competitionRef}>
      <CompetitionPropsContext.Provider
        value={{
          data: { ...competition, enteredOn, isCompetitionPerksOnly, isUserPerks },
          isLoading,
          error,
        }}
      >
        {data?.fields ? <StackSchema {...(contentfulApiCompetitionToStackSchema(data) as StackSchemaProps)} /> : null}
        {children}
      </CompetitionPropsContext.Provider>
    </div>
  );
};

export const useCompetitionProps = (): UseContentProps<CompetitionProps> => {
  const context = useContext(CompetitionPropsContext);
  if (context === undefined || context === null) {
    throw new Error('useCompetitionProps must be used within a CompetitionPropsProvider');
  }

  return context;
};
